/**
 * Dynamic state manager - mechanism that enables setting and getting state values from different parts of the codebase.
 *
 * Since we don't have a central state management, nor we have a single JS entry point, we need an app-wide mechanism that
 * enables asynchronous setting and getting of state values.
 *
 * This primitive mechanism is a suboptimal solution that was added to solve problems with displaying interstitials and
 * AI product tour (tooltips) at the same time.
 *
 * The long-term solution to this problem is to move state management to the backend. For example, by introducing a
 * "backend-for-frontend" endpoint that returns information about which dynamic UI element should be shown to the user.
 *
 * [USAGE]
 * ```javascript
 * import { getDynamicState, setDynamicState } from 'Utils/dynamic-state';
 *
 * setDynamicState('example', { status: true });
 *
 * console.log(getDynamicState('example')) // { status: true }
 *
 * window.addEventListener('dynamicstatechange', (ev) => {
 *     console.log(ev.detail);
 * });
 * ```
 */

export function getDynamicState(uid) {
    window.__VUMEDI_DYNAMIC__ = window.__VUMEDI_DYNAMIC__ || {};

    return window.__VUMEDI_DYNAMIC__[uid];
}

export function setDynamicState(uid, data) {
    window.__VUMEDI_DYNAMIC__ = window.__VUMEDI_DYNAMIC__ || {};
    window.__VUMEDI_DYNAMIC__[uid] = data;

    const stateEvent = new CustomEvent('dynamicstatechange', { detail: { uid, data } });

    window.dispatchEvent(stateEvent);
}

/**
 * Interstitial specific global state entries. Used by the tooltip mechanism to determine whether to show or hide
 * the AI product tour.
 *
 * Possible `data` values:
 *
 * - undefined : the request/logic to determine whether to display the interstitial is not done yet
 * - true      : the conclusion is to display the interstitial
 * - false     : the conclusion is to NOT display the interstitial
 */
export function getInterstitialStates() {
    return [
        {
            uid: 'update-site-and-subscriptions-modal',
            data: getDynamicState('update-site-and-subscriptions-modal'),
        },
        {
            uid: 'update-subspecialty-subscriptions-modal',
            data: getDynamicState('update-subspecialty-subscriptions-modal'),
        },
        {
            // This modal is exception because it doesn't rely on API response - hidden by default
            uid: 'wl-invite-modal',
            data: getDynamicState('wl-invite-modal') || false,
        },
    ];
}
